import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/navigation/AdminNavigation';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Image from '../../components/AppImage';
import Icon from '../../components/AppIcon';
import { contentService } from '../../services/contentService';

const HallOfFameEditor = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    rating: 0,
    displayOrder: 0,
    isFeatured: false
  });

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const data = await contentService?.getAll();
      setContent(data?.sort((a, b) => b?.rating - a?.rating));
    } catch (err) {
      setError(err?.message || 'Erro ao carregar conteúdo');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      rating: item?.rating || 0,
      displayOrder: item?.displayOrder || 0,
      isFeatured: item?.isFeatured || false
    });
  };

  const handleSave = async () => {
    if (!editingItem) return;

    try {
      await contentService?.update(editingItem?.id, formData);
      setEditingItem(null);
      loadContent();
    } catch (err) {
      setError(err?.message || 'Erro ao salvar alterações');
    }
  };

  const handleCancel = () => {
    setEditingItem(null);
    setFormData({ rating: 0, displayOrder: 0, isFeatured: false });
  };

  return (
    <>
      <Helmet>
        <title>Editar Hall da Fama - UP MIDIA Admin</title>
        <meta name="description" content="Gerencie estrelas e posições do Hall da Fama" />
      </Helmet>
      <div className="min-h-screen bg-background pb-20">
        <div className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="container mx-auto px-4 py-4">
            <AdminNavigation />
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
              Editar Hall da Fama
            </h1>
            <p className="text-muted-foreground">
              Ajuste as estrelas e posições dos conteúdos em destaque
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-500">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin glow-primary"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {content?.map((item) => (
                <div
                  key={item?.id}
                  className="bg-card rounded-lg p-6 glow-primary hover:glow-primary-hover transition-smooth"
                >
                  <div className="flex items-start gap-6">
                    <Image
                      src={item?.thumbnail}
                      alt={item?.thumbnailAlt}
                      className="w-32 h-32 object-cover rounded-md"
                    />

                    <div className="flex-1">
                      <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                        {item?.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        {item?.description}
                      </p>

                      {editingItem?.id === item?.id ? (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-foreground mb-2">
                                Estrelas (0-5)
                              </label>
                              <Input
                                type="number"
                                min="0"
                                max="5"
                                step="0.1"
                                value={formData?.rating}
                                onChange={(e) => setFormData(prev => ({ ...prev, rating: parseFloat(e?.target?.value) || 0 }))}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-foreground mb-2">
                                Posição
                              </label>
                              <Input
                                type="number"
                                min="0"
                                value={formData?.displayOrder}
                                onChange={(e) => setFormData(prev => ({ ...prev, displayOrder: parseInt(e?.target?.value) || 0 }))}
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="isFeatured"
                              checked={formData?.isFeatured}
                              onChange={(e) => setFormData(prev => ({ ...prev, isFeatured: e?.target?.checked }))}
                              className="w-4 h-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring"
                            />
                            <label htmlFor="isFeatured" className="text-sm font-medium text-foreground">
                              Marcar como Destaque
                            </label>
                          </div>
                          <div className="flex items-center gap-3">
                            <Button onClick={handleSave} className="glow-primary">
                              Salvar
                            </Button>
                            <Button variant="ghost" onClick={handleCancel}>
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                              <Icon name="Star" size={20} color="var(--color-primary)" />
                              <span className="text-lg font-bold text-foreground">
                                {item?.rating?.toFixed(1)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Icon name="ArrowUpDown" size={20} color="var(--color-muted-foreground)" />
                              <span className="text-sm text-muted-foreground">
                                Posição: {item?.displayOrder}
                              </span>
                            </div>
                            {item?.isFeatured && (
                              <div className="flex items-center gap-2">
                                <Icon name="Award" size={20} color="var(--color-accent)" />
                                <span className="text-sm text-accent font-medium">
                                  Destaque
                                </span>
                              </div>
                            )}
                          </div>
                          <Button
                            onClick={() => handleEdit(item)}
                            iconName="Edit"
                            variant="ghost"
                          >
                            Editar
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default HallOfFameEditor;